// RITA-1525 — Blockhöhen im Browser messen und daraus die Seiten berechnen.
//
// Die Blöcke werden in einem unsichtbaren Messbereich mit der Breite und Schrift des Textbereichs
// gerendert; nach dem Layout liest der Hook die Höhen und ruft die reine Funktion seitenAufteilen.
// Keine Beobachter (ResizeObserver/MutationObserver): Die Breite ist fest, gemessen wird nur bei
// Änderung von Dokument, Wertemodus, Geometrie oder nachdem die Schrift geladen ist. Damit kann
// sich die Messung nicht über ihre eigene Ausgabe selbst antreiben (apps/frontend/docs/HOTSPOTS.md).
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import BlockAnsicht from '../components/BlockAnsicht';
import { abstandsstil } from '../model/abstaende';
import { Wertemodus } from '../model/platzhalterwerte';
import { Vertragsdokument } from '../model/types';
import { Seitengeometrie } from './seitengeometrie';
import { Blockmass, Seiteninhalt, seitenAufteilen } from './seitenAufteilen';

export interface Seitenaufteilung {
  seiten: Seiteninhalt[];
  /** Muss im Baum gerendert werden, damit gemessen werden kann. */
  messung: React.ReactElement;
}

function useSeitenaufteilung(
  dokument: Vertragsdokument,
  modus: Wertemodus,
  geometrie: Seitengeometrie,
): Seitenaufteilung {
  const messungRef = useRef<HTMLDivElement>(null);
  const [schriftBereit, setSchriftBereit] = useState(false);
  const [seiten, setSeiten] = useState<Seiteninhalt[]>([]);

  useEffect(() => {
    let aktiv = true;
    const schriften = typeof document !== 'undefined' ? document.fonts : undefined;
    if (schriften && schriften.ready) {
      schriften.ready.then(() => {
        if (aktiv) setSchriftBereit(true);
      });
    } else {
      setSchriftBereit(true);
    }
    return () => {
      aktiv = false;
    };
  }, []);

  useLayoutEffect(() => {
    const container = messungRef.current;
    if (!container) return;
    const masse: Blockmass[] = dokument.text.map((block) => {
      const element = container.querySelector<HTMLElement>(`[data-block-id="${block.id}"]`);
      return {
        id: block.id,
        hoehePx: element ? element.getBoundingClientRect().height : 0,
        seitenumbruch: block.typ === 'seitenumbruch',
      };
    });
    setSeiten(seitenAufteilen(masse, geometrie.inhaltHoehePx));
  }, [dokument, modus, geometrie, schriftBereit]);

  const messung = (
    <div
      ref={messungRef}
      className="vertrag-messung"
      aria-hidden="true"
      data-testid="vertrag-messung"
      style={{
        width: `${geometrie.inhaltBreiteMm}mm`,
        fontSize: `${dokument.schrift.groessePt}pt`,
        ...abstandsstil(dokument.schrift),
      }}
    >
      {dokument.text.map((block) => (
        <BlockAnsicht key={block.id} block={block} modus={modus} />
      ))}
    </div>
  );

  return { seiten, messung };
}

export default useSeitenaufteilung;
